import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import Pagination from '../components/Pagination';
import './Orders.css';

const localeMap = { lt: 'lt-LT', en: 'en-US', de: 'de-DE' };

function Orders() {
  const { t, language } = useLanguage();
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['my-orders'],
    queryFn: async () => {
      const res = await apiClient.get('/orders/my-orders');
      return res.data;
    },
  });

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  if (isLoading) {
    return (
      <div className="orders-page">
        <div className="orders-container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  const orders = data?.orders || [];
  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  // Pagination calculations
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: t('pending'), color: '#f59e0b', bg: '#fef3c7' },
      processing: { label: t('orderStatusProcessing'), color: '#3b82f6', bg: '#dbeafe' },
      shipped: { label: t('orderStatusShipped'), color: '#6366f1', bg: '#e0e7ff' },
      delivered: { label: t('orderStatusDelivered'), color: '#10b981', bg: '#d1fae5' },
      cancelled: { label: t('orderStatusCancelled'), color: '#ef4444', bg: '#fee2e2' },
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const locale = localeMap[language] || 'lt-LT';
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="orders-page">
      <div className="orders-container">
        <div className="orders-header">
          <h1>{t('myOrdersTitle')}</h1>
          <p className="orders-subtitle">
            {orders.length > 0 
              ? t('orderCount').replace('{n}', orders.length)
              : t('orderHistory')}
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-orders">
            <div className="empty-orders-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12l2 2 4-4"></path>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3z"></path>
                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3z"></path>
                <path d="M12 3c0 1-1 3-3 3S6 4 6 3s1-3 3-3 3 2 3 3z"></path>
                <path d="M12 21c0-1-1-3-3-3S6 20 6 21s1 3 3 3 3-2 3-3z"></path>
              </svg>
            </div>
            <h2>{t('noOrders')}</h2>
            <p>{t('firstOrderHere')}</p>
            <Link to="/products" className="btn btn-primary">
              {t('viewProducts')}
            </Link>
          </div>
        ) : (
          <>
            <div className="orders-filters">
              <button
                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                {t('all')} ({orders.length})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
                onClick={() => setFilterStatus('pending')}
              >
                {t('pending')} ({orders.filter(o => o.status === 'pending').length})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'processing' ? 'active' : ''}`}
                onClick={() => setFilterStatus('processing')}
              >
                {t('orderStatusProcessing')} ({orders.filter(o => o.status === 'processing').length})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'shipped' ? 'active' : ''}`}
                onClick={() => setFilterStatus('shipped')}
              >
                {t('orderStatusShipped')} ({orders.filter(o => o.status === 'shipped').length})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'delivered' ? 'active' : ''}`}
                onClick={() => setFilterStatus('delivered')}
              >
                {t('orderStatusDelivered')} ({orders.filter(o => o.status === 'delivered').length})
              </button>
            </div>

            <div className="orders-list">
              {filteredOrders.length === 0 ? (
                <div className="no-orders-filtered">
                  <p>{t('noOrdersFiltered')}</p>
                </div>
              ) : (
                paginatedOrders.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  return (
                    <Link
                      key={order.id}
                      to={`/orders/${order.id}`}
                      className="order-card"
                    >
                      <div className="order-card-header">
                        <div className="order-number-section">
                          <span className="order-label">{t('orderLabel')}</span>
                          <h3 className="order-number">#{order.order_number}</h3>
                        </div>
                        <div className="order-status-badge" style={{ 
                          backgroundColor: statusInfo.bg,
                          color: statusInfo.color 
                        }}>
                          {statusInfo.label}
                        </div>
                      </div>

                      <div className="order-card-body">
                        <div className="order-info-row">
                          <div className="order-info-item">
                            <span className="order-info-label">{t('orderDate')}</span>
                            <span className="order-info-value">{formatDate(order.created_at)}</span>
                          </div>
                          <div className="order-info-item">
                            <span className="order-info-label">{t('time')}</span>
                            <span className="order-info-value">{formatTime(order.created_at)}</span>
                          </div>
                          <div className="order-info-item">
                            <span className="order-info-label">{t('items')}</span>
                            <span className="order-info-value">{order.item_count || 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="order-card-footer">
                        <div className="order-total-section">
                          <span className="order-total-label">{t('total')}</span>
                          <span className="order-total-amount">
                            {order.total ? parseFloat(order.total).toFixed(2) : '0.00'} €
                          </span>
                        </div>
                        <div className="order-arrow">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                          </svg>
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>

            {filteredOrders.length > 0 && totalPages > 1 && (
              <div className="orders-pagination">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  limit={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onLimitChange={() => {}}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Orders;
